import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { createPortal } from "react-dom"
import {
	QueryClient,
	QueryClientProvider,
	useQuery,
	useQueryClient
} from "@tanstack/react-query"
import Spinner from "../Spinner";
import { generateThreadUrl, timeAgo } from "./utils";
import Topics from "../../../libraries/threads";
import UserAvatar from "../UserAvatar";

const qc = new QueryClient()

function NotificationsInner() {
	const [open, setOpen] = useState(false)
	const buttonRef = useRef(null)
	const panelRef = useRef(null)

	const queryClient = useQueryClient()

	const {
		data: notifications = [],
		isLoading,
		refetch
	} = useQuery({
		queryKey: ["notifications"],
		queryFn: async () => {
			const res = await ApiClient("/topics-notification-list")
			return res.data
		},
	})

	useEffect(() => {
		if (!Topics.TOPICS_USER_NOTIFICATIONS_CHANNEL) return;
		const channel = Topics.TOPICS_USER_NOTIFICATIONS_CHANNEL
		const handler = (data) => {
			// refetch notifications
			refetch()
		}
		channel.bind('topics.user.notifications', handler)
		return () => channel.unbind('topics.user.notifications', handler)
	}, [])

	useEffect(() => {
		if (open) refetch()

		const handler = e => {
			if (
				open &&
				panelRef.current &&
				!panelRef.current.contains(e.target) &&
				buttonRef.current &&
				!buttonRef.current.contains(e.target)
			) {
				setOpen(false)
			}
		}
		document.addEventListener("click", handler)
		return () => document.removeEventListener("click", handler)
	}, [open])

	const markAsRead = async id => {
		await ApiClient.post(`/topics-notification-list/${id}/mark-read`)

		queryClient.setQueryData(["notifications"], prev =>
			prev.map(n => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
		)
	}

	const markAllAsRead = async () => {
		await ApiClient.post(`/topics-notification-list/mark-read-all`)

		queryClient.setQueryData(["notifications"], prev =>
			prev.map(n => ({ ...n, read_at: new Date().toISOString() }))
		)
	}

	const hasUnread = notifications.some(n => !n.read_at)

	return (
		<a
			ref={buttonRef}
			className="relative cursor-pointer"
			onClick={() => setOpen(o => !o)}
		>
			<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24">
				<g fill="none" stroke="currentColor" strokeWidth="2">
					<path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12c0 1.6.376 3.112 1.043 4.453c.178.356.237.763.134 1.148l-.595 2.226a1.3 1.3 0 0 0 1.591 1.592l2.226-.596a1.63 1.63 0 0 1 1.149.133A9.96 9.96 0 0 0 12 22Z" />
					<path strokeLinecap="round" d="M8 10.5h8M8 14h5.5" />
				</g>
			</svg>

			{hasUnread && (
				<span className="absolute top-1 right-1 w-2 h-2 bg-secondary rounded-full"></span>
			)}

			{createPortal(
				<AnimatePresence>
					{open && (
						<motion.div
							ref={panelRef}
							initial={{ y: -20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							exit={{ y: -20, opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="absolute right-6 top-header mt-2 w-full max-w-96 overflow-clip bg-white shadow-primary rounded-primary border-thin border-grey z-50"
						>
							<div className="flex flex-col text-black p-6 py-4 border-b-thin border-grey relative">
								<p className="text-sub font-medium">Notifications</p>
								<p className="text-article text-grey-text">For comments</p>

								{/* read all notifications */}
								{notifications.length > 0 && hasUnread && (
									<a
										onClick={e => {
											e.preventDefault();
											e.stopPropagation();
											markAllAsRead()
										}}
										className="absolute bottom-2 right-2 text-xs px-2 py-1 text-primary hover:underline cursor-pointer"
									>
										Mark all as Read
									</a>
								)}
							</div>

							<div className="overflow-y-auto max-h-[400px] w-full divide-y divide-black divide-opacity-10">
								{isLoading ? (
									<div className="p-8 text-center text-black opacity-60">
										<Spinner />
									</div>
								) : notifications.length === 0 ? (
									<div className="p-8 text-center flex flex-col items-center justify-center text-primary gap-4">
										<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
											<g fill="none" stroke="currentColor" strokeWidth="2">
												<path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12c0 1.6.376 3.112 1.043 4.453c.178.356.237.763.134 1.148l-.595 2.226a1.3 1.3 0 0 0 1.591 1.592l2.226-.596a1.63 1.63 0 0 1 1.149.133A9.96 9.96 0 0 0 12 22Z" />
												<path strokeLinecap="round" d="M8 10.5h8M8 14h5.5" />
											</g>
										</svg>

										<p className="text-grey-text text-article">
											No notifications yet
										</p>
									</div>
								) : (
									notifications.map(n => (
										<a
											key={n.id}
											href={`${generateThreadUrl(n.thread)}`}
											className="relative group block p-4 px-6 hover:bg-primary-50 transition-colors bg-white [&_p]:truncate"
											onClick={() => markAsRead(n.id)}
										>
											<div className="flex items-start gap-3">
												{n.actor && (
													<UserAvatar name={n.actor.full_name} src={n.actor.avatar_path} />
												)}

												<div className="flex-1 max-w-96">
													{n.actor && (
														<p className="text-sm font-medium text-black">
															{n.actor.full_name}
														</p>
													)}

													<p className="text-sm text-black opacity-70 mt-1">
														{n.type === "mention"
															? "mentioned you in a comment"
															: n.type === "reply"
																? "replied to your comment"
																: n.type === "thread-created"
																	? "created a new thread"
																	: "added a new comment to a thread"}
													</p>

													{n.thread && (
														<p className="text-xs text-black opacity-50 mt-1">
															in {n.thread.title}
														</p>
													)}

													<p className="text-xs text-black opacity-40 mt-1">
														{timeAgo(n.created_at)}
													</p>
												</div>

												{!n.read_at && (
													<div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
												)}


												{/* Mark as Read Button */}
												{!n.read_at && (
													<a
														onClick={e => {
															e.preventDefault();
															e.stopPropagation();
															markAsRead(n.id)
														}}
														className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs px-2 py-1 text-primary hover:underline cursor-pointer"
													>
														Mark as Read
													</a>
												)}
											</div>
										</a>
									))
								)}
							</div>
						</motion.div>
					)}
				</AnimatePresence>,
				document.body
			)}
		</a>
	)
}

// Export wrapper
export default function Notifications() {
	return (
		<QueryClientProvider client={qc}>
			<NotificationsInner />
		</QueryClientProvider>
	)
}
